import { useState, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./calculator.module.scss";
import CalculatorUnitSelector from "./CalculatorUnitSelector";

const CalculatorFeetInchesInput = ({
  label,
  feetId,
  inchesId,
  feetValue,
  inchesValue,
  onFeetChange,
  onInchesChange,
  error,
  unitOptions,
  unitValue,
  onUnitChange,
  isWebLayout = false,
  ...rest
}) => {
  const [showUnitSelector, setShowUnitSelector] = useState(false);
  const { forceDropdown, ...inputDomRest } = rest;

  const handleUnitSelectorClick = () => {
    setShowUnitSelector(true);
  };

  const handleUnitSelectorClose = () => {
    setShowUnitSelector(false);
  };

  const handleUnitSelect = (newUnit) => {
    if (onUnitChange) {
      onUnitChange({ target: { value: newUnit } });
    }
    setShowUnitSelector(false);
  };

  const getUnitLabel = () => {
    if (unitOptions && unitOptions.length > 0 && unitValue) {
      const selectedOption = unitOptions.find((opt) => opt.value === unitValue);
      return selectedOption?.shortLabel || selectedOption?.label || unitValue;
    }
    return "in";
  };

  const feetInputRef = useRef(null);
  const inchesInputRef = useRef(null);

  const handleFocus = (e) => {
    setTimeout(() => {
      if (e.target && typeof e.target.scrollIntoView === "function") {
        e.target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 100);
  };

  return (
    <div className={styles.formField}>
      <label className={styles.fieldLabel}>{label}</label>
      <div className={styles.feetInchesRow}>
        <div className={styles.feetInchesGroup}>
          <div className={styles.inputContainer + (error ? " " + styles.inputContainerError : "")}>
            <input
              className={styles.input}
              id={feetId}
              value={feetValue}
              onChange={onFeetChange}
              onFocus={handleFocus}
              type="number"
              min="0"
              autoComplete="off"
              inputMode="decimal"
              placeholder=""
              aria-label={`${label} feet`}
              ref={feetInputRef}
              {...inputDomRest}
            />
            <span className={styles.unitDropdownText}>ft</span>
          </div>
        </div>
        <div className={styles.feetInchesGroup}>
          <div
            className={
              styles.inputContainer +
              (error ? " " + styles.inputContainerError : "") +
              (isWebLayout ? " " + styles.inputContainerRelative : "")
            }
          >
            <input
              className={styles.input}
              id={inchesId}
              value={inchesValue}
              onChange={onInchesChange}
              onFocus={handleFocus}
              type="number"
              min="0"
              max="11"
              step="0.1"
              autoComplete="off"
              inputMode="decimal"
              placeholder=""
              aria-label={`${label} inches`}
              ref={inchesInputRef}
              {...inputDomRest}
            />
            <div className={styles.unitSelectorContainer}>
              {unitOptions && unitOptions.length > 1 ? (
                <button
                  type="button"
                  className={styles.unitSelector}
                  onClick={handleUnitSelectorClick}
                  aria-label={`Change unit for ${label}`}
                >
                  <span className={styles.unitDropdownText}>{getUnitLabel()}</span>
                  <span
                    className={`${styles.unitDropdownArrow} ${showUnitSelector ? styles.unitDropdownArrowOpen : ""}`}
                  >
                    <i className="tg-icons-ChevronDown"></i>
                  </span>
                </button>
              ) : (
                <span className={styles.unitDropdownText}>in</span>
              )}
            </div>

            {/* Render dropdown inside container for proper positioning */}
            {unitOptions && unitOptions.length > 1 && (
              <CalculatorUnitSelector
                visible={showUnitSelector}
                units={unitOptions}
                selected={unitValue}
                onSelect={handleUnitSelect}
                onClose={handleUnitSelectorClose}
                isWebLayout={isWebLayout}
                {...rest}
              />
            )}
          </div>
        </div>
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

CalculatorFeetInchesInput.propTypes = {
  label: PropTypes.string.isRequired,
  feetId: PropTypes.string.isRequired,
  inchesId: PropTypes.string.isRequired,
  feetValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  inchesValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onFeetChange: PropTypes.func.isRequired,
  onInchesChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  unitOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired,
      shortLabel: PropTypes.string,
    }),
  ),
  unitValue: PropTypes.string,
  onUnitChange: PropTypes.func,
  isWebLayout: PropTypes.bool,
};

export default CalculatorFeetInchesInput;
